import { ApolloError, useQuery } from '@apollo/client';
import { createContext, useContext, useMemo } from 'react';
import { AnyFinancialEntity } from '../../financialEntities';
import { Goal } from '../../goals';
import {
    AllFinancialEntitiesDocument,
    FinancialEntityDocument,
    GoalDocument,
} from '../../graphql/generated';
import { PageMode } from '../../types';
import { usePageContext } from '../pageMode';
import { isSingleplayer, useCurrentUser } from '../user';
import { isMyFE } from './helpers';

export type FEsData = {
    goals: Goal[];
    financialEntities: AnyFinancialEntity[];
};

interface FEs {
    data: undefined | FEsData;
    unfilteredData: undefined | FEsData;
    loading: boolean;
    error?: ApolloError | undefined;
    refetch: () => Promise<any>;
}

interface FinancialEntitiesContext {
    fes: FEs;
}

export const FinancialEntitiesCtx = createContext<FinancialEntitiesContext>({
    fes: {
        data: undefined,
        unfilteredData: undefined,
        loading: true,
        refetch: async () => {},
    },
});

export const useFinancialEntitiesContext = (): FinancialEntitiesContext => {
    return useContext(FinancialEntitiesCtx);
};

// Fetch FEs (balances & entity details only)
// Filter by public/mine view
export const useFinancialEntities = (): FEs => {
    const currentUser = useCurrentUser();
    const { pageMode } = usePageContext();
    const { data, loading, error, refetch } = useQuery(
        AllFinancialEntitiesDocument
    );
    const noPartnership =
        !currentUser.activePartnershipId || isSingleplayer(currentUser);

    return useMemo(() => {
        if (!data) {
            return { data, unfilteredData: undefined, loading, error, refetch };
        }

        const allGoals = (
            (data.goals ?? []).filter(
                (g) => !g.deletedAt && !g.isHidden
            ) as Goal[]
        ).map((g) => ({
            ...g,
            financialEntities: g.financialEntities.filter(filterDeleted),
        }));

        const allFEs = (data.financialEntities ?? []).filter(
            filterDeleted
        ) as AnyFinancialEntity[];

        // If user does not have an active partnership, show all FEs & goals.
        if (noPartnership) {
            return {
                data: { goals: allGoals, financialEntities: allFEs },
                unfilteredData: { goals: allGoals, financialEntities: allFEs },
                loading,
                error,
                refetch,
            };
        }

        // Otherwise, filter FEs & goals based on page mode.
        const filteredGoals = filterGoalsByShareStatus(pageMode, allGoals).map(
            (g) => filterGoalAccountsByShareStatus(pageMode, g)
        );
        const filteredFEs = allFEs.filter((fe) =>
            filterByShareStatus(pageMode, fe)
        );

        return {
            data: { goals: filteredGoals, financialEntities: filteredFEs },
            unfilteredData: { goals: allGoals, financialEntities: allFEs },
            loading,
            error,
            refetch,
        };
    }, [data, loading, error, pageMode, noPartnership, refetch]);
};

export const useFinancialEntity = (variables: {
    id: string;
}): { fe?: AnyFinancialEntity; loading: boolean } => {
    const { data, loading } = useQuery(FinancialEntityDocument, {
        variables,
    });
    if (loading || !data?.financialEntity) return { loading: true };
    const fe = data.financialEntity as AnyFinancialEntity;
    return { fe, loading: false };
};

export const useGoal = (variables: {
    id: string;
}): { goal?: Goal; unfilteredGoal?: Goal; loading: boolean } => {
    const { activePartnershipId } = useCurrentUser();
    const { pageMode } = usePageContext();
    const { data, loading } = useQuery(GoalDocument, {
        variables,
    });
    if (loading || !data?.goal) return { loading: true };
    let goal = data.goal as Goal;
    goal = {
        ...goal,
        financialEntities: goal.financialEntities.filter(filterDeleted),
    };
    return {
        // Only filter goal accounts if user is part of an active partnership.
        // Otherwise, always show all goal accounts.
        goal: activePartnershipId
            ? filterGoalAccountsByShareStatus(pageMode, goal)
            : goal,
        unfilteredGoal: goal,
        loading: false,
    };
};

/**
 * Filters goals by share status, returning only goals applicable to
 * the given pageMode.
 * NOTE: Does *not* filter their child entities (like goal accounts or goal
 * account onboardings) by share status.
 */
export const filterGoalsByShareStatus = (
    pageMode: PageMode,
    goals: Goal[]
): Goal[] => {
    return goals.filter((g) => filterByShareStatus(pageMode, g));
};

const filterDeleted = <T extends { deletedAt?: string | null | undefined }>(
    e: T
): boolean => !e.deletedAt;

const filterByShareStatus = (
    pageMode: PageMode,
    e: AnyFinancialEntity | Goal
) => {
    const isPrivate = e.shareStatus === 'PRIVATE';

    // If "public" mode, return goals and FEs that aren't private
    if (pageMode === 'PUBLIC') {
        return !isPrivate;
    }

    // If "mine" mode, return
    //  (1) all goals that are *not* Joint
    //  (2) all FEs owned by current user

    if (e.__typename === 'Goal') {
        return e.accountStructure !== 'JOINT';
    }
    return e.__typename === 'FinancialEntity' && isMyFE(e);
};

const filterGoalAccountsByShareStatus = (pageMode: PageMode, g: Goal): Goal => {
    // If "public" mode, show all goal accounts for goal
    if (pageMode === 'PUBLIC') return g;

    // If "mine" mode, only show goal accounts owned by current user
    const myFinancialEntities = g.financialEntities.filter((fe) => isMyFE(fe));
    return { ...g, financialEntities: myFinancialEntities };
};

export const useHasNoFEs = () => {
    const { fes } = useFinancialEntitiesContext();
    return fes.unfilteredData?.financialEntities.length === 0;
};
